import { useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { videojs } from "../../lib/videojs";

export interface ExamPreviewPlayerProps {
  src: string;
  type: string;
  subtitlesUrl?: string;
}

export interface ExamPreviewPlayerHandler {
  seekTo(timeSeconds: number, autoplay?: boolean): void;
}

export const ExamPreviewPlayer = forwardRef<
  ExamPreviewPlayerHandler,
  ExamPreviewPlayerProps
>(function ExamPreviewPlayerImpl(props, ref) {
  const videoRef = useRef<HTMLVideoElement>();
  const playerRef = useRef<ReturnType<typeof videojs>>();

  useImperativeHandle(ref, () => ({
    seekTo(timeSeconds: number, autoplay?: boolean) {
      playerRef.current?.ready(() => {
        playerRef.current?.currentTime(timeSeconds);
        if (autoplay) {
          playerRef.current?.play();
        } else {
          playerRef.current?.pause();
        }
      });
    },
  }));

  useEffect(() => {
    const opt = {
      fill: true, // https://videojs.com/guides/layout/#fill-mode
      playbackRates: [2.0, 1.75, 1.5, 1.25, 1.0, 0.75, 0.5],
      disablePictureInPicture: true,
      controlBar: {
        pictureInPictureToggle: false,
        remainingTimeDisplay: false,
      },
    };
    playerRef.current = videojs(videoRef.current, opt);
    playerRef.current.reloadSourceOnError();
    playerRef.current.hlsQualitySelector();
    playerRef.current.ready(() => {
      // https://stackoverflow.com/a/49598388
      const textTrackSettings = playerRef.current.textTrackSettings;
      textTrackSettings.setValues({ backgroundOpacity: "0.5" });
      textTrackSettings.updateDisplay();
    });

    return () => {
      playerRef.current?.dispose();
      playerRef.current = null;
    };
  }, []);

  return (
    <div className="p-exam__side-panel_player-container">
      <video
        ref={videoRef}
        className="video-js"
        controls
        onContextMenu={(e) => e.preventDefault()}
      >
        <source src={props.src} type={props.type} />
        {props.subtitlesUrl && (
          <track
            src={props.subtitlesUrl}
            srcLang="ja"
            label="日本語"
            kind="subtitles"
            default={true}
          />
        )}
      </video>
    </div>
  );
});
